import React, { type ReactNode, useContext, useId, useState } from "react";
import type { BaseProps } from "@purpur/common-types";
import { Heading, type HeadingTagType } from "@purpur/heading";
import { IconChevronDown } from "@purpur/icon/chevron-down";
import { Paragraph } from "@purpur/paragraph";
import c from "classnames/bind";

import { AccordionNegativeContext } from "./accordion";
import styles from "./accordion-item.module.scss";

const cx = c.bind(styles);

const rootClassName = "purpur-accordion-item";

export type AccordionItemOnClickEvent = React.MouseEvent<HTMLButtonElement, MouseEvent> & {
  currentTarget: { dataset: { state: "open" | "closed" } };
};

export type AccordionItemProps = Omit<BaseProps, "onClick" | "title" | "children"> & {
  children: ReactNode;
  /**
   * If true, the children will take the full width without max-width constraint.
   * @default false
   */
  fullWidth?: boolean;
  /**
   * Content to be rendered inside a `button` element.
   * Only pass ReactNodes that are valid as children of a `button`, such as plain text,
   * inline elements like `span` or other non-interactive elements. Avoid block-level or interactive elements.
   */
  title: ReactNode;
  /**
   * This is the heading tag used for the accordion item title. Defaults to h3.
   * `type HeadingTagType = "h1" | "h2" | "h3" | "h4" | "h5" | "h6"`
   */
  titleTag?: HeadingTagType;
  /**
   * Makes it possible to set the initial state to opened
   * */
  initialOpen?: boolean;
  /**
   * onClick is called when accordion item is expanded or collapsed
   * event.currentTarget.dataset.state will be either "open" or "closed"
   */
  onClick?: (event: AccordionItemOnClickEvent) => void;
  onOpen?: () => void;
  onClose?: () => void;
};

export const AccordionItem = ({
  children,
  className,
  fullWidth = false,
  title,
  titleTag = "h3",
  initialOpen = false,
  onClick,
  onOpen,
  onClose,
  ...props
}: AccordionItemProps) => {
  const negative = useContext(AccordionNegativeContext);
  const uid = useId();
  const [hidden, setHidden] = useState(!initialOpen);

  const classes = cx([
    rootClassName,
    {
      [`${rootClassName}--negative`]: negative,
    },
    className,
  ]);

  return (
    <div className={classes} {...props}>
      <Heading
        tag={titleTag}
        variant="title-100"
        className={cx([`${rootClassName}__header`, `${rootClassName}__title`])}
      >
        <button
          type="button"
          data-state={hidden ? "open" : "close"} // The state is actually set in the onClick, so reversing the data- output state here
          onClick={(e: AccordionItemOnClickEvent) => {
            onClick && onClick(e);
            if (hidden) {
              onOpen && onOpen();
            } else {
              onClose && onClose();
            }
            setHidden(!hidden);
          }}
          className={cx(`${rootClassName}__trigger`, className, {
            [`${rootClassName}__rotate`]: !hidden,
          })}
          aria-expanded={!hidden}
          aria-controls={uid}
          id={uid + "button"}
        >
          {title}
          <IconChevronDown size="md" className={cx(`${rootClassName}__icon`)} aria-hidden />
        </button>
      </Heading>
      <div
        className={cx(`${rootClassName}__content`, className)}
        aria-hidden={hidden}
        id={uid}
        aria-labelledby={uid + "button"}
        role="region"
      >
        <div
          className={cx(`${rootClassName}__contentText`, {
            [`${rootClassName}__contentText--limit-width`]: !fullWidth,
          })}
        >
          {typeof children === "string" ? (
            <Paragraph variant="paragraph-200">{children}</Paragraph>
          ) : (
            children
          )}
        </div>
      </div>
    </div>
  );
};
